/*
 *  Copyright (c) 2003-2019 GameDuell GmbH, All Rights Reserved
 *  This document is strictly confidential and sole property of GameDuell GmbH, Berlin, Germany
 */
import { LocalStore, User } from '@gd/storage'
import { Balance } from '@/model/UserData'
import { Zone } from '@/model/Zone'
import { TournamentsZones } from '@/model/ZoneSelection'
import { ERROR_CODES } from '@/model/ErrorCodes'
import { track } from '@gd/tracking'
import { Commit, Dispatch } from 'vuex'

/**
 * ACTIONS: are the "interface" to the BE - it contains all the great functionlity to aggregate the
 * required data.
 *
 * Recommended to read before using: https://vuex.vuejs.org/guide/actions.html
 */

const actions = {
  loadBalance({ commit }: { commit: Commit }) {
    return fetch('/gd/rest/me/balance', {
      credentials: 'same-origin',
      cache: 'no-cache',
    })
      .then((res) => {
        if (res.status >= 400) {
          commit('error', { id: 'loadBalance', responseStatus: res.status })
          throw { errorCode: res.status === 401 ? ERROR_CODES.UNAUTHORIZED : ERROR_CODES.UNKNOWN }
        }
        return res.json()
      })
      .then((balance: Balance) => {
        commit('applyUserBalance', balance)
      })
  },
  loadUser({ commit }: { commit: Commit }) {
    const storage = new LocalStore({})
    return new Promise((res: (value?: unknown) => void, rej: (reason?: unknown) => void) => {
      if (storage.user.id) {
        commit('applyUser', storage.user)
        res()
      } else {
        fetch('/gd/rest/me/userData', {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          cache: 'no-cache',
        })
          .then((res) => {
            if (res.status >= 400) {
              commit('error', { id: 'loadUser', responseStatus: res.status })
              throw { errorCode: res.status === 401 ? ERROR_CODES.UNAUTHORIZED : ERROR_CODES.UNKNOWN }
            }
            return res.json()
          })
          .then((userData: GD_me.UserData) => {
            const storageUser: User = {
              ...(storage.user && {
                payout: storage.user.payout,
                forcePayout: storage.user.forcePayout,
                bonusVisualization: storage.user.bonusVisualization,
                boku: false,
                avatarUrl: '',
              }),
              actorType: userData.actorType,
              currency: userData.gdCurrency,
              id: userData.userId,
              // TODO... comming soon
              locale: '',
              // TODO the string is different as what the storageUser expects
              loyalty: userData.loyalty,
              name: userData.userName,
              ...(userData.forcePayout && { forcePayout: userData.forcePayout }),
            }
            commit('applyUser', storageUser)
            res()
          })
          .catch((err) => {
            rej(err)
          })
      }
    })
  },
  fetchZoneTeasers({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
    return fetch(process.env.VUE_APP_REST_URL + 'zones', {
      credentials: 'same-origin',
      cache: 'no-cache',
    })
      .then((res: any) => {
        if (res.status >= 400) {
          commit('error', { id: 'fetchZoneTeasers', responseStatus: res.status })
          throw { errorCode: res.status === 401 ? ERROR_CODES.UNAUTHORIZED : ERROR_CODES.UNKNOWN }
        }
        return res.json()
      })
      .then((data: TournamentsZones) => {
        // see mutations:
        commit('applyZonesTeasers', data.zones)

        if (data.announcement) {
          commit('applyAnnouncement', data.announcement)
        }

        dispatch('lobbyTimers/stopAllTimers')

        data.zones
          .filter((zone) => zone.tournamentInfo !== null)
          .forEach((zone) => {
            const info = zone.tournamentInfo
            commit('lobbyTimers/setState', {
              tournamentId: info.tournamentId,
              secondsToStart: info.dates.secondsUntilLobbyStart,
              secondsToEnd: info.dates.secondsUntilLobbyEnd,
            })
            dispatch('lobbyTimers/invokeStartTimer', info.tournamentId)
          })
      })
  },
  fetchTournament({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, zoneId: string) {
    return fetch(process.env.VUE_APP_REST_URL + 'zone/' + zoneId, {
      credentials: 'same-origin',
      cache: 'no-cache',
    })
      .then((res: any) => {
        if (res.status > 202) {
          commit('error', { id: 'fetchTournament', responseStatus: res.status })
          throw { errorCode: res.status >= 400 ? res.status : ERROR_CODES.UNKNOWN }
        }
        return res.json()
      })
      .then((data: Zone) => {
        commit('applyZone', data)
        dispatch('lobbyTimers/stopAllTimers')

        data.tournaments.forEach((tmnt) => {
          commit('lobbyTimers/setState', {
            tournamentId: tmnt.tournamentId,
            secondsToStart: tmnt.dates.secondsUntilLobbyStart,
            secondsToEnd: tmnt.dates.secondsUntilLobbyEnd,
          })
          dispatch('lobbyTimers/invokeStartTimer', tmnt.tournamentId)

          if (tmnt.dates.secondsUntilLobbyStart < 0) {
            track({
              key: 'tournaments',
              id: 'fetchTournament',
              data: 'secondsUntilLobbyStart_negativeValue_' + tmnt.dates.secondsUntilLobbyStart,
            })
          }
          if ((tmnt.dates as any).secondsUntilLobbyEnd < 0) {
            track({
              key: 'tournaments',
              id: 'fetchTournament',
              data: 'secondsUntilLobbyEnd_negativeValue_' + (tmnt.dates as any).secondsUntilLobbyEnd,
            })
          }
        })

        if (data.tournaments.length <= 0) {
          commit('resetActiveTournament')
        }
      })
  },
}

export { actions }
